import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { Plus, X, Package, ChevronRight, CheckCircle, AlertTriangle, Search } from 'lucide-react';


const COLORS = ['BLACK','GREY','RED','WHITE','BROWN','MULTICOLOR','OTHER'];
const GRADES = ['A','B','C'];
const QUALITY = ['Good','Average','Damaged'];
const COMPLAINTS = ['White Line','Crack','Band','Color Change','Rough Surface','Other'];

const statusBadge = s => {
  const map = {
    RECEIVED:'badge-blue', IN_PROCESS:'badge-amber',
    COMPLETED:'badge-green', REJECTED:'badge-red',
    AVAILABLE:'badge-blue', IN_GANGSAW:'badge-amber',
    IN_PROCESS2:'badge-amber', QC_PENDING:'badge-amber',
    PASSED:'badge-green', SOLD:'badge-purple',
  };
  const labels = { IN_GANGSAW:'In Gangsaw', QC_PENDING:'QC Pending', IN_PROCESS:'In Process' };
  return <span className={`badge ${map[s]||'badge-muted'}`}>{labels[s]||s?.replace(/_/g,' ')}</span>;
};

// ─── BLOCK REGISTRATION FORM ─────────────────────────────────────────────────
const BlockForm = ({ onSave, onCancel }) => {
  const [form, setForm] = useState({
    color:'BLACK', length:'', width:'', height:'',
    weight:'', quality_grade:'A', quarry_location:'', notes:''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = await api.post('/production/blocks/', form);
      onSave(res.data.data || res.data);
    } catch { setError('Failed to register block.'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-body">
        {error && <div className="error-box">{error}</div>}

        {/* Size Visual */}
        <div style={{ background:'var(--bg-hover)', borderRadius:'var(--radius-sm)', padding:14, marginBottom:16, textAlign:'center' }}>
          <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'1px', marginBottom:8 }}>Block Size (Standard: 10.5 × 8.8 × Height)</div>
          <div style={{ fontFamily:'Syne', fontSize:20, fontWeight:700, color:'var(--gold)' }}>
            {form.length||'?'} × {form.width||'?'} × {form.height||'?'} <span style={{ fontSize:13, color:'var(--text-muted)' }}>cm</span>
          </div>
          {form.length && form.width && form.height && (
            <div style={{ fontSize:12, color:'var(--green)', marginTop:6 }}>
              Volume: {(parseFloat(form.length)*parseFloat(form.width)*parseFloat(form.height)/1000000).toFixed(3)} m³
            </div>
          )}
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Granite Color *</label>
            <select className="form-input" required value={form.color} onChange={e => set('color',e.target.value)}>
              {COLORS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Quality Grade *</label>
            <select className="form-input" required value={form.quality_grade} onChange={e => set('quality_grade',e.target.value)}>
              {GRADES.map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Length (cm) *</label>
            <input className="form-input" type="number" required value={form.length} onChange={e => set('length',e.target.value)} placeholder="e.g. 300"/>
          </div>
          <div className="form-group">
            <label className="form-label">Width (cm) *</label>
            <input className="form-input" type="number" required value={form.width} onChange={e => set('width',e.target.value)} placeholder="e.g. 220"/>
          </div>
          <div className="form-group">
            <label className="form-label">Height (cm) *</label>
            <input className="form-input" type="number" required value={form.height} onChange={e => set('height',e.target.value)} placeholder="e.g. 180"/>
          </div>
          <div className="form-group">
            <label className="form-label">Weight (Tonnes)</label>
            <input className="form-input" type="number" step="0.01" value={form.weight} onChange={e => set('weight',e.target.value)} placeholder="e.g. 12.5"/>
          </div>
          <div className="form-group">
            <label className="form-label">Quarry Location</label>
            <input className="form-input" value={form.quarry_location} onChange={e => set('quarry_location',e.target.value)} placeholder="e.g. North Face"/>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-input" value={form.notes} onChange={e => set('notes',e.target.value)} placeholder="Any remarks"/>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Register Block'}</button>
      </div>
    </form>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ProductionPage() {
  const [tab, setTab]             = useState('blocks');
  const [blocks, setBlocks]       = useState([]);
  const [parts, setParts]         = useState([]);
  const [slabs, setSlabs]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [blockForm, setBlockForm]       = useState({
    color:'', quality_grade:'A', length:'', width:'', height:'',
    weight:'', direction:'N', received_date:'', notes:'',
    status:'COMPLETED', block_type:'OLD_STOCK',
    price_per_cft:'', source_reference:'', condition_notes:'',
  });
  const [savingBlock, setSavingBlock] = useState(false);
  const setB = (k,v) => setBlockForm(f=>({...f,[k]:v}));
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilter] = useState('');
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [msg, setMsg]             = useState('');
  const [error, setError]         = useState('');

  // QC Modal state
  const [qcSlab, setQcSlab]       = useState(null);
  const [qcData, setQcData]       = useState({ status:'PASSED', quality_grade:'A', has_defects:false, defect_notes:'', complaints:[], price_per_sqft:'' });

  const showMsg = (m, isErr=false) => { isErr ? setError(m) : setMsg(m); setTimeout(() => { setMsg(''); setError(''); }, 4000); };

  const loadBlocks = useCallback(() => {
    setLoading(true);
    api.get(`/production/blocks/?search=${search}${filterStatus?`&status=${filterStatus}`:''}`)
      .then(r => setBlocks(r.data.results||[]))
      .finally(() => setLoading(false));
  }, [search, filterStatus]);

  const handleAddBlock = async e => {
    e.preventDefault();
    setSavingBlock(true);
    try {
      await api.post('/production/blocks/', { ...blockForm, status:'COMPLETED' });
      setShowAddBlock(false);
      loadBlocks();
    } catch(err) {
      alert(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save.');
    } finally { setSavingBlock(false); }
  };

  const loadParts = useCallback(() => {
    setLoading(true);
    api.get(`/production/parts/?search=${search}${filterStatus?`&status=${filterStatus}`:''}`)
      .then(r => setParts(r.data.results||[]))
      .finally(() => setLoading(false));
  }, [search, filterStatus]);

  const loadSlabs = useCallback(() => {
    setLoading(true);
    api.get(`/production/slabs/?search=${search}${filterStatus?`&status=${filterStatus}`:''}`)
      .then(r => setSlabs(r.data.results||[]))
      .finally(() => setLoading(false));
  }, [search, filterStatus]);

  useEffect(() => {
    if (tab==='blocks') loadBlocks();
    else if (tab==='parts') loadParts();
    else if (tab==='slabs') loadSlabs();
  }, [tab, search, filterStatus]);

  const handleCompleteBlock = async (block) => {
    if (!window.confirm(`Complete block ${block.block_number}?\n\nThis will auto-create 4 Parts (P1–P4) immediately.`)) return;
    try {
      await api.patch(`/production/blocks/${block.id}/`, { status:'COMPLETED' });
      showMsg(`✅ Block ${block.block_number} completed — 4 Parts auto-created!`);
      loadBlocks();
    } catch { showMsg('Failed to complete block.', true); }
  };

  const handleSendToGangsaw = async (part) => {
    if (!window.confirm(`Send ${part.part_code} to Gangsaw?\n\nThis will auto-create 9 Slabs (G1–G9).`)) return;
    try {
      await api.patch(`/production/parts/${part.id}/`, { status:'IN_GANGSAW' });
      showMsg(`✅ ${part.part_code} sent to Gangsaw — 9 Slabs auto-created!`);
      loadParts();
    } catch { showMsg('Failed to update part.', true); }
  };

  const handleQcSubmit = async () => {
    if (!qcSlab) return;
    try {
      await api.patch(`/production/slabs/${qcSlab.id}/`, {
        ...qcData,
        defect_notes: qcData.complaints.join(', ') + (qcData.defect_notes ? ` — ${qcData.defect_notes}` : ''),
      });
      showMsg(`✅ Slab ${qcSlab.slab_code} QC ${qcData.status === 'PASSED' ? 'Passed ✓' : 'Rejected ✗'}`);
      setQcSlab(null);
      loadSlabs();
    } catch { showMsg('QC update failed.', true); }
  };

  const toggleComplaint = c => {
    setQcData(d => ({
      ...d,
      complaints: d.complaints.includes(c) ? d.complaints.filter(x=>x!==c) : [...d.complaints, c]
    }));
  };

  // Tab styles
  const tabStyle = active => ({
    padding:'10px 20px', fontSize:13, fontWeight:600, cursor:'pointer', border:'none',
    background: active ? 'var(--gold)' : 'var(--bg-hover)',
    color: active ? '#0a0c10' : 'var(--text-secondary)',
    borderRadius: 'var(--radius-sm)', transition:'all 0.2s',
  });

  return (
    <Layout
      title="Production"
      subtitle="BOX → PART → GANGSAW → STOCK flow"
      actions={<div style={{display:'flex',gap:8}}>
        <button className="btn btn-secondary" onClick={()=>setShowAddBlock(true)}>
          <Plus size={14}/>Add Old Block
        </button>
        <button className="btn btn-primary" onClick={()=>setShowBlockForm(true)}>
          <Plus size={14}/>New Session
        </button>
      </div>}
    >
      {msg   && <div className="success-box mb-16">{msg}</div>}
      {error && <div className="error-box mb-16">{error}</div>}

      {/* Flow indicator */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {[
          { label:'BOX', sub:'Raw Blocks', count: blocks.length, color:'var(--blue)' },
          { label:'PART', sub:'P1–P4 each', count: parts.length, color:'var(--gold)' },
          { label:'GANGSAW', sub:'G1–G9 slabs', count: slabs.length, color:'var(--green)' },
          { label:'STOCK', sub:'Ready to sell', count: slabs.filter(s=>s.status==='PASSED').length, color:'var(--amber)' },
        ].map((item, i) => (
          <React.Fragment key={i}>
            <div style={{ textAlign:'center', padding:'12px 16px', background:'var(--bg-card)', border:`1px solid ${item.color}30`, borderRadius:'var(--radius-sm)', minWidth:90 }}>
              <div style={{ fontFamily:'Syne', fontSize:22, fontWeight:700, color:item.color }}>{item.count}</div>
              <div style={{ fontSize:12, fontWeight:700, color:item.color }}>{item.label}</div>
              <div style={{ fontSize:10, color:'var(--text-muted)' }}>{item.sub}</div>
            </div>
            {i < 3 && <ChevronRight size={18} style={{ color:'var(--border-light)', flexShrink:0 }}/>}
          </React.Fragment>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <button style={tabStyle(tab==='blocks')} onClick={() => { setTab('blocks'); setFilter(''); }}>📦 Blocks (BOX)</button>
        <button style={tabStyle(tab==='parts')}  onClick={() => { setTab('parts');  setFilter(''); }}>🔧 Parts (P1–P4)</button>
        <button style={tabStyle(tab==='slabs')}  onClick={() => { setTab('slabs');  setFilter(''); }}>💎 Gangsaw Slabs</button>
      </div>

      <div className="card">
        {/* Filter bar */}
        <div className="filter-bar">
          <div className="search-wrap">
            <Search size={14}/>
            <input className="search-input" placeholder={`Search ${tab}...`} value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
          <select className="form-input" style={{ width:'auto', minWidth:160 }} value={filterStatus} onChange={e => setFilter(e.target.value)}>
            <option value="">All Status</option>
            {tab==='blocks' && <><option value="RECEIVED">Received</option><option value="IN_PROCESS">In Process</option><option value="COMPLETED">Completed</option><option value="REJECTED">Rejected</option></>}
            {tab==='parts'  && <><option value="AVAILABLE">Available</option><option value="IN_GANGSAW">In Gangsaw</option><option value="COMPLETED">Completed</option></>}
            {tab==='slabs'  && <><option value="IN_PROCESS">In Process</option><option value="QC_PENDING">QC Pending</option><option value="PASSED">Passed</option><option value="REJECTED">Rejected</option><option value="SOLD">Sold</option></>}
          </select>
        </div>

        {loading ? <div className="loading"><div className="spinner"/>Loading...</div> : (

          // ── BLOCKS TABLE ──────────────────────────────────────────────────
          tab === 'blocks' && (
            blocks.length === 0
              ? <div className="empty-state"><Package size={40}/><p>No blocks registered. Click "Register Block" to start.</p></div>
              : <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>Block #</th><th>Color</th><th>Grade</th>
                      <th>Size (cm)</th><th>Volume</th><th>Weight</th>
                      <th>Location</th><th>Parts</th><th>Status</th><th>Action</th>
                    </tr></thead>
                    <tbody>
                      {blocks.map(b => (
                        <tr key={b.id}>
                          <td style={{ color:'var(--gold)', fontWeight:600 }}>{b.block_number}</td>
                          <td style={{ fontWeight:600, color:'var(--text-primary)' }}>{b.color}</td>
                          <td><span className="badge badge-gold">Grade {b.quality_grade||'—'}</span></td>
                          <td style={{ fontFamily:'monospace', fontSize:12 }}>{b.length}×{b.width}×{b.height}</td>
                          <td style={{ color:'var(--blue)' }}>{b.volume_cubic_cm ? `${(b.volume_cubic_cm/1000000).toFixed(3)}m³` : '—'}</td>
                          <td>{b.weight ? `${b.weight}T` : '—'}</td>
                          <td>{b.quarry_location||'—'}</td>
                          <td>
                            <span className={`badge ${b.part_count>0?'badge-green':'badge-muted'}`}>
                              {b.part_count}/4
                            </span>
                          </td>
                          <td>{statusBadge(b.status)}</td>
                          <td>
                            {(b.status==='RECEIVED'||b.status==='IN_PROCESS') && (
                              <button className="btn btn-sm btn-primary" onClick={() => handleCompleteBlock(b)}>
                                <CheckCircle size={11}/> Complete → Split
                              </button>
                            )}
                            {b.status==='COMPLETED' && <span style={{ fontSize:11, color:'var(--green)' }}>✓ 4 Parts created</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
          )
        )}

        {!loading && tab === 'parts' && (
          parts.length === 0
            ? <div className="empty-state"><Package size={40}/><p>No parts found. Complete a Block to auto-create parts.</p></div>
            : <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th>Part Code</th><th>Part #</th><th>Size (cm)</th>
                    <th>Grade</th><th>Slabs</th><th>Status</th><th>Action</th>
                  </tr></thead>
                  <tbody>
                    {parts.map(p => (
                      <tr key={p.id}>
                        <td style={{ color:'var(--gold)', fontWeight:600 }}>{p.part_code}</td>
                        <td><span className="badge badge-blue">{p.part_number}</span></td>
                        <td style={{ fontFamily:'monospace', fontSize:12 }}>{p.length||'—'}×{p.width||'—'}×{p.height||'—'}</td>
                        <td><span className="badge badge-gold">Grade {p.quality_grade||'—'}</span></td>
                        <td><span className={`badge ${p.slab_count>0?'badge-green':'badge-muted'}`}>{p.slab_count}/9</span></td>
                        <td>{statusBadge(p.status)}</td>
                        <td>
                          {p.status==='AVAILABLE' && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleSendToGangsaw(p)}>
                              Send to Gangsaw →
                            </button>
                          )}
                          {p.status==='IN_GANGSAW' && <span style={{ fontSize:11, color:'var(--amber)' }}>⚙️ In Gangsaw</span>}
                          {p.status==='COMPLETED' && <span style={{ fontSize:11, color:'var(--green)' }}>✓ 9 Slabs created</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
        )}

        {!loading && tab === 'slabs' && (
          slabs.length === 0
            ? <div className="empty-state"><Package size={40}/><p>No slabs found. Send a Part to Gangsaw to create slabs.</p></div>
            : <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th>Slab Code</th><th>Slab #</th><th>Size (cm)</th>
                    <th>Thickness</th><th>Area (sqft)</th><th>Quality</th>
                    <th>Price/sqft</th><th>Status</th><th>Action</th>
                  </tr></thead>
                  <tbody>
                    {slabs.map(s => (
                      <tr key={s.id}>
                        <td style={{ color:'var(--gold)', fontWeight:600 }}>{s.slab_code}</td>
                        <td><span className="badge badge-purple">{s.slab_number}</span></td>
                        <td style={{ fontFamily:'monospace', fontSize:12 }}>{s.length||'—'}×{s.width||'—'}</td>
                        <td>{s.thickness ? `${s.thickness}mm` : '—'}</td>
                        <td>{s.area_sqft||'—'}</td>
                        <td><span className={`badge ${s.quality_grade==='A'?'badge-green':s.quality_grade==='B'?'badge-amber':'badge-red'}`}>Grade {s.quality_grade||'—'}</span></td>
                        <td>{s.price_per_sqft ? `₹${s.price_per_sqft}` : '—'}</td>
                        <td>{statusBadge(s.status)}</td>
                        <td>
                          {(s.status==='IN_PROCESS'||s.status==='QC_PENDING') && (
                            <button className="btn btn-sm btn-secondary" onClick={() => {
                              setQcSlab(s);
                              setQcData({ status:'PASSED', quality_grade:'A', has_defects:false, defect_notes:'', complaints:[], price_per_sqft:'' });
                            }}>
                              QC Check
                            </button>
                          )}
                          {s.status==='PASSED' && <span style={{ fontSize:11, color:'var(--green)' }}>✓ In Stock</span>}
                          {s.status==='SOLD'   && <span style={{ fontSize:11, color:'var(--amber)' }}>💰 Sold</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
        )}
      </div>

      {/* BLOCK REGISTER MODAL */}
      {showBlockForm && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowBlockForm(false)}>
          <div className="modal" style={{ maxWidth:620 }}>
            <div className="modal-header">
              <div>
                <h3 style={{ fontSize:15, fontWeight:700 }}>Register New Block (BOX)</h3>
                <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>Block ID will be auto-generated: BLK/YYYY/0001</p>
              </div>
              <button onClick={() => setShowBlockForm(false)} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer' }}><X size={18}/></button>
            </div>
            <BlockForm
              onSave={block => { setShowBlockForm(false); showMsg(`Block ${block.block_number} registered!`); loadBlocks(); }}
              onCancel={() => setShowBlockForm(false)}
            />
          </div>
        </div>
      )}

      {/* QC CHECK MODAL */}
      {qcSlab && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setQcSlab(null)}>
          <div className="modal" style={{ maxWidth:560 }}>
            <div className="modal-header">
              <div>
                <h3 style={{ fontSize:15, fontWeight:700 }}>Quality Check — {qcSlab.slab_code}</h3>
                <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>Slab {qcSlab.slab_number} · Size: {qcSlab.length||'?'}×{qcSlab.width||'?'} cm</p>
              </div>
              <button onClick={() => setQcSlab(null)} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer' }}><X size={18}/></button>
            </div>
            <div className="modal-body">

              {/* Pass / Fail toggle */}
              <div style={{ display:'flex', gap:12, marginBottom:16 }}>
                {[['PASSED','✅ Pass','var(--green)','var(--green-dim)'],['REJECTED','❌ Reject','var(--red)','var(--red-dim)']].map(([val,label,color,bg]) => (
                  <button key={val} type="button" onClick={() => setQcData(d=>({...d,status:val}))} style={{
                    flex:1, padding:'12px', borderRadius:'var(--radius-sm)', border:`2px solid ${qcData.status===val?color:'var(--border)'}`,
                    background: qcData.status===val ? bg : 'var(--bg-primary)', cursor:'pointer',
                    fontSize:14, fontWeight:700, color: qcData.status===val ? color : 'var(--text-muted)',
                  }}>{label}</button>
                ))}
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Quality Grade</label>
                  <select className="form-input" value={qcData.quality_grade} onChange={e => setQcData(d=>({...d,quality_grade:e.target.value}))}>
                    {GRADES.map(g => <option key={g} value={g}>Grade {g} — {g==='A'?'Good':g==='B'?'Average':'Damaged'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Price per sqft (₹)</label>
                  <input className="form-input" type="number" value={qcData.price_per_sqft} onChange={e => setQcData(d=>({...d,price_per_sqft:e.target.value}))} placeholder="e.g. 150"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Slab Thickness (mm)</label>
                  <input className="form-input" type="number" value={qcData.thickness||''} onChange={e => setQcData(d=>({...d,thickness:e.target.value}))} placeholder="e.g. 20"/>
                </div>
              </div>

              {/* Complaints */}
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:10 }}>Complaints / Defects (select all that apply)</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {COMPLAINTS.map(c => {
                    const active = qcData.complaints.includes(c);
                    return (
                      <button key={c} type="button" onClick={() => toggleComplaint(c)} style={{
                        padding:'6px 14px', borderRadius:'20px', fontSize:12, fontWeight:500, cursor:'pointer',
                        border:`1px solid ${active?'var(--red)':'var(--border-light)'}`,
                        background: active ? 'var(--red-dim)' : 'var(--bg-primary)',
                        color: active ? 'var(--red)' : 'var(--text-secondary)',
                      }}>
                        {active ? '✓ ' : ''}{c}
                      </button>
                    );
                  })}
                </div>
              </div>

              {qcData.complaints.length > 0 && (
                <div className="form-group" style={{ marginTop:12 }}>
                  <label className="form-label">Additional Notes on Defects</label>
                  <textarea className="form-input" rows={2} value={qcData.defect_notes} onChange={e => setQcData(d=>({...d,defect_notes:e.target.value}))} placeholder="Describe the defects in detail"/>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setQcSlab(null)}>Cancel</button>
              <button type="button" className={`btn ${qcData.status==='PASSED'?'btn-primary':'btn-danger'}`} onClick={handleQcSubmit}>
                {qcData.status==='PASSED' ? '✅ Confirm Pass → Add to Stock' : '❌ Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Old Block Modal */}
      {showAddBlock && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAddBlock(false)}>
          <div className="modal" style={{maxWidth:580,maxHeight:'90vh',overflowY:'auto'}}>
            <div className="modal-header">
              <h3>Add Old Block to Production</h3>
              <button onClick={()=>setShowAddBlock(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18}}>×</button>
            </div>
            <form onSubmit={handleAddBlock}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Color *</label>
                    <input className="form-input" required value={blockForm.color} onChange={e=>setB('color',e.target.value)} placeholder="e.g. Black, Grey"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Direction</label>
                    <select className="form-input" value={blockForm.direction} onChange={e=>setB('direction',e.target.value)}>
                      <option value="N">North</option>
                      <option value="S">South</option>
                      <option value="E">East</option>
                      <option value="W">West</option>
                      <option value="G">General</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Length (cm)</label>
                    <input className="form-input" type="number" step="0.01" value={blockForm.length} onChange={e=>setB('length',e.target.value)} placeholder="e.g. 250"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Width (cm)</label>
                    <input className="form-input" type="number" step="0.01" value={blockForm.width} onChange={e=>setB('width',e.target.value)} placeholder="e.g. 150"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Height (cm)</label>
                    <input className="form-input" type="number" step="0.01" value={blockForm.height} onChange={e=>setB('height',e.target.value)} placeholder="e.g. 100"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Weight (tons)</label>
                    <input className="form-input" type="number" step="0.01" value={blockForm.weight} onChange={e=>setB('weight',e.target.value)} placeholder="e.g. 5.5"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Quality Grade</label>
                    <select className="form-input" value={blockForm.quality_grade} onChange={e=>setB('quality_grade',e.target.value)}>
                      <option value="A">Grade A</option>
                      <option value="B">Grade B</option>
                      <option value="C">Grade C</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Block Type</label>
                    <select className="form-input" value={blockForm.block_type||'OLD_STOCK'} onChange={e=>setB('block_type',e.target.value)}>
                      <option value="OLD_STOCK">Old Stock</option>
                      <option value="REGULAR">Regular</option>
                      <option value="IMPERFECT">Imperfect</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price per Cubic Feet (₹)</label>
                    <input className="form-input" type="number" step="0.01" value={blockForm.price_per_cft||''} onChange={e=>setB('price_per_cft',e.target.value)} placeholder="0.00"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Received Date</label>
                    <input className="form-input" type="date" value={blockForm.received_date} onChange={e=>setB('received_date',e.target.value)}/>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Source / Origin</label>
                    <input className="form-input" value={blockForm.source_reference||''} onChange={e=>setB('source_reference',e.target.value)} placeholder="e.g. Old quarry stock"/>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Condition Notes</label>
                    <textarea className="form-input" rows={2} value={blockForm.condition_notes||''} onChange={e=>setB('condition_notes',e.target.value)} placeholder="e.g. Minor crack, slightly uneven"/>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows={2} value={blockForm.notes} onChange={e=>setB('notes',e.target.value)} placeholder="Any additional notes"/>
                  </div>
                </div>
               </div>
               <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddBlock(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingBlock}>{savingBlock?'Saving...':'Add Block'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
